import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Visibility of one part of the layout. */
export interface LayoutElementSettings {
  hidden: boolean;
}

/** @deprecated Use LayoutElementSettings. */
export type NavSettings = LayoutElementSettings;
/** @deprecated Use LayoutElementSettings. */
export type FooterSettings = LayoutElementSettings;

/**
 * Visibility of each element the master page renders around the router outlet. One key per
 * element, so the navbar's two breakpoints are named as such rather than as bare 'mobile' and
 * 'desktop', which read as breakpoints of the whole layout rather than of the navbar.
 */
export interface LayoutSettings {
  mobileNavbar: LayoutElementSettings;
  desktopNavbar: LayoutElementSettings;
  footer: LayoutElementSettings;
}

/**
 * Default layout settings
 */
export const layoutSettings: LayoutSettings = {
  mobileNavbar: {
    hidden: false,
  },
  desktopNavbar: {
    hidden: false,
  },
  footer: {
    hidden: false,
  },
};

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  private _document = inject(DOCUMENT);

  private _onLayoutChanged: BehaviorSubject<LayoutSettings> = new BehaviorSubject(layoutSettings);

  constructor() {
    // Reflect the starting state, so a page loaded straight onto a route that hides something is
    // laid out correctly on its first paint rather than after the first change.
    this._reflectToDocument(this._onLayoutChanged.getValue());
  }

  /**
   * Mirrors what the shell is currently showing onto the document element, as classes stylesheets
   * can hang layout off - #main subtracts only the chrome that is actually there, so a hidden
   * navbar or footer stops reserving its height.
   *
   * Done here rather than in the component that renders the shell: every consumer then gets it by
   * calling the service, with nothing to remember and no second place for the two to disagree.
   */
  private _reflectToDocument(settings: LayoutSettings): void {
    const root = this._document?.documentElement;
    if (!root) {
      return;
    }

    root.classList.toggle('mobile-navbar-none', settings.mobileNavbar.hidden);
    root.classList.toggle('desktop-navbar-none', settings.desktopNavbar.hidden);
    root.classList.toggle('footer-none', settings.footer.hidden);
  }

  /**
   * Get layout settings
   * @returns Observable<LayoutSettings>
   */
  get onLayoutChanged(): Observable<LayoutSettings> {
    return this._onLayoutChanged.asObservable();
  }

  /**
   * Set the visibility of one part of the layout
   * @param section which part to show or hide
   * @param hidden whether it is hidden
   */
  private _setVisibility(section: keyof LayoutSettings, hidden: boolean): void {
    const currentValue = this._onLayoutChanged.getValue();
    this._publish({ ...currentValue, [section]: { hidden } });
  }

  /**
   * Publishes a new layout state. Every mutator goes through here so the document classes cannot
   * drift out of step with what subscribers were told.
   */
  private _publish(settings: LayoutSettings): void {
    this._reflectToDocument(settings);
    this._onLayoutChanged.next(settings);
  }

  /**
   * Set layout settings
   * @param config
   */
  setConfig(config: Partial<LayoutSettings>): void {
    const currentValue = this._onLayoutChanged.getValue();
    this._publish({ ...currentValue, ...config });
  }

  /**
   * Hide the mobile navbar
   */
  hideMobileNavbar(): void {
    this._setVisibility('mobileNavbar', true);
  }

  /**
   * Show the mobile navbar
   */
  showMobileNavbar(): void {
    this._setVisibility('mobileNavbar', false);
  }

  /**
   * Hide the desktop navbar
   */
  hideDesktopNavbar(): void {
    this._setVisibility('desktopNavbar', true);
  }

  /**
   * Show the desktop navbar
   */
  showDesktopNavbar(): void {
    this._setVisibility('desktopNavbar', false);
  }

  /** @deprecated Use hideMobileNavbar. */
  hideMobileNav(): void {
    this.hideMobileNavbar();
  }

  /** @deprecated Use showMobileNavbar. */
  showMobileNav(): void {
    this.showMobileNavbar();
  }

  /** @deprecated Use hideDesktopNavbar. */
  hideDesktopNav(): void {
    this.hideDesktopNavbar();
  }

  /** @deprecated Use showDesktopNavbar. */
  showDesktopNav(): void {
    this.showDesktopNavbar();
  }

  /**
   * Hide the footer
   */
  hideFooter(): void {
    this._setVisibility('footer', true);
  }

  /**
   * Show the footer
   */
  showFooter(): void {
    this._setVisibility('footer', false);
  }

  /**
   * Restore layout settings to default
   */
  restoreDefaults(): void {
    this._publish(layoutSettings);
  }
}
